
import React, { useContext } from 'react';

import { AppContext } from '@r-app/app-context';
import { Layer } from '@r-core/types';
import { renderTracking } from '@r-services/render-tracking';
import { svgPolygonFromVectors } from '../helpers/svg-polygon-from-vectors';
import { convertRect2Vectors, getPanelItemVectors } from '@r-core/helpers';
import { Components } from '@rta/rta-blueprint-library';

type RenderPanelMaskProps = {
	islandId: string | null;
	panelId: string | null;
	options: {
		includeCountertop: boolean;
	}
};

export const RenderPanelMask = (props: RenderPanelMaskProps): React.ReactElement => {
	const { islandId, panelId, options } = props;
	const { includeCountertop } = options;
	const { controller: { kitchen }} = useContext(AppContext);
	const island = kitchen.design.islands.findById(islandId);
	const panel = island.panels.findById(panelId);

	renderTracking.incrementRender('RenderIslandOverheadMask');

	const panelSize = panel.size;
	const panelCenter = panel.center.clone().roundNearestSixteenth();

	const countertop = includeCountertop ? island.getCountertop() : null;
	const countertopHeight = includeCountertop && countertop ? countertop.size.depth : 0;

	const panelVectors = convertRect2Vectors({
		layer: Layer.Panel,
		center: { x: 0, y: panelCenter.y - (countertopHeight / 2) },
		width: panelSize.width,
		height: panelSize.height + countertopHeight,
		angle: 0 // panel.getOriginAngle()
	});

	/* Create mask data for island panelItems with a distanceAboveCountertop > 0 */
	const panelMaskData = [
			{
			id: panel.id,
			center: panelCenter,
			angle: -panel.getOriginAngle(),
			panelItemGroups: panel.panelItemGroups.children.map((panelItemGroup) => {
				return {
					id: panelItemGroup.id,
					center: panelItemGroup.center.roundNearestSixteenth(),
					angle: 0,
					panelItems: panelItemGroup.items.children.map((panelItem) => {
						const corners = getPanelItemVectors({ panelItem, isCountertop: false }).filter((v) => v.layer === Layer.Panel);

						// center is zero when item is full height, default center is based on item placement
						// in the case of full height, we want to override y to 0 for cutouts
						const center = panelItem.getCenter(true).roundNearestSixteenth();
						if (panelItem.isFullHeightAppliance) center.y = 0;

						// Model rotation (stored in radians) -- convert to degrees for SVG rotation
						// For front panel 2D view, y-axis rotation = visual rotation; x is 3D-only
						const { y: mrY, z: mrZ } = panelItem.effectiveRotation;
						const modelRotationDeg = (mrZ || mrY) * (180 / Math.PI);

						const hasShowImage = panelItem.item.component?.showImage ?? false;
						const isMokVent = /^MOK-VENT-/.test(panelItem.item.sku ?? '');
						const isSpacer = panelItem.item.category?.isSpacer ?? false;
						const isTrimKit = panelItem.item.sku?.endsWith('TK') ?? false;
						if (!isMokVent && !isSpacer && !isTrimKit && (panelItem.item.placement !== Components.PLACEMENT_TOP_ONLY || hasShowImage)) {
							const svgCutout = panelItem.item.component.svg?.cutout({ fill: 'white', opacity: 1, diameter: panelItem.item.cutout.width }) ?? null;
							return {
								id: panelItem.id,
								center,
								angle: svgCutout ? modelRotationDeg : 0,
								svgPolygon: svgCutout ?? svgPolygonFromVectors(corners, { fill: 'white', opacity: 1 })
							};
						}
						return null;
					}).filter((item) => item !== null)
				};
			}).filter((group) => group.panelItems.length > 0)
		}
	].filter((group) => group.panelItemGroups.length > 0);

	// don't bother with an empty mask
	if (panelMaskData.length === 0) return null;

	/* Render the masks for panelItem Cutouts */
	const masks = panelMaskData.map((data) => {
		return <g
			key={`${data.id}-mask`}
			type="panel-group-mask"
		>{
			data.panelItemGroups.map((group) => {
				return <g
					key={`${group.id}-mask`}
					type="panel-item-group-mask"
					transform={`translate(${group.center.x}, ${panelCenter.y})`}
				>{
					group.panelItems.map((item) => {
						return <g
							key={`${item.id}-mask`}
							type="panel-item-mask"
							transform={`translate(${item.center.x}, ${item.center.y})${item.angle !== 0 ? ` rotate(${item.angle})` : ''}`}
						>{
							item.svgPolygon
						}</g>;
					})
				}</g>;
			})
		}</g>;
	});


	/* Render the masks for island panelItem Cutouts */
	// return <defs>
	// 	<mask id={`panel-mask-${panelId}`}>
	// 		{/* <rect width={island.islandSize.width} height={island.islandSize.depth} fill="white" /> */}
	// 		{ svgPolygonFromVectors(panelVectors, { fill: 'white' }) }
	// 		{ masks }
	// 	</mask>
	// </defs>;

	// temp fix so the shadows don't overlap the mask
	return <>{masks}</>;
};
